import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/format';
import { BarChart3, TrendingUp, ShoppingBag } from 'lucide-react';

/**
 * Minimal SVG bar chart of the seller's last 30 days: revenue + orders.
 * No chart library needed — keeps the bundle small and the visual language
 * cohesive with the marketplace theme (slate / orange).
 */
export const SellerSalesChart = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/sellers/me/analytics').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { series, total_orders, total_revenue } = data;
  const maxRev = Math.max(1, ...series.map((s) => s.revenue));
  const maxOrders = Math.max(1, ...series.map((s) => s.orders));

  return (
    <section data-testid="seller-sales-chart" className="border border-slate-200 bg-white rounded-md p-5 mt-6">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5">
            <BarChart3 className="h-3 w-3" /> Analítica de ventas
          </div>
          <h3 className="font-display text-lg font-black text-slate-900 mt-1">Últimos 30 días</h3>
        </div>
        <div className="flex gap-3">
          <div className="border border-slate-200 px-3 py-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><ShoppingBag className="h-3 w-3" /> Órdenes</div>
            <div className="font-display font-black text-xl text-slate-900 mt-0.5">{total_orders}</div>
          </div>
          <div className="border border-orange-200 bg-orange-50 px-3 py-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-orange-700 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Ingresos</div>
            <div className="font-display font-black text-xl text-slate-900 mt-0.5">{formatCOP(total_revenue)}</div>
          </div>
        </div>
      </div>

      {/* Bars */}
      <div className="flex items-end h-40 gap-1 border-b border-l border-slate-200 pb-1 pl-1 overflow-hidden">
        {series.map((s) => {
          const rev = (s.revenue / maxRev) * 100;
          const ord = (s.orders / maxOrders) * 100;
          return (
            <div
              key={s.date}
              className="group relative flex-1 h-full flex flex-col justify-end items-center gap-0.5"
              title={`${s.date}: ${s.orders} órdenes · ${formatCOP(s.revenue)}`}
            >
              <div
                className="w-full bg-orange-400 hover:bg-orange-500 transition-colors"
                style={{ height: `${rev}%`, minHeight: rev > 0 ? '2px' : '0' }}
              />
              <div
                className="w-full bg-slate-700 hover:bg-slate-900 transition-colors"
                style={{ height: `${ord * 0.3}%`, minHeight: ord > 0 ? '2px' : '0' }}
              />
              <div className="absolute -top-8 hidden group-hover:block bg-slate-900 text-white text-[10px] font-mono px-2 py-1 whitespace-nowrap z-10">
                {s.date.slice(5)}: {formatCOP(s.revenue)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        <div className="flex items-center gap-1"><span className="h-2 w-3 bg-orange-400 inline-block" /> Ingresos diarios</div>
        <div className="flex items-center gap-1"><span className="h-2 w-3 bg-slate-700 inline-block" /> Órdenes</div>
      </div>
    </section>
  );
};
